import { IError } from '@/hooks/use-link-social-network/use-link-social-network.hook'
import { Environment } from '@/hub-shared'
import { ModalStatusTypes, SocialNetworksAvailables } from '@/interfaces/socialnetworks.interface'
import onEnvironmentChange from '@/observables/environment.observable'
import defaultImage from '@assets/img/jpg/avatar.jpg'
import Button from '@components/buttons/button/button.component'
import ErrorMessage from '@enums/error-messages.enum'
import localForageHelper from '@helpers/localforage.helper'
import User, { FacebookDiscoveryUser } from '@interfaces/user.interface'
import onSocialNetworkConnection from '@observables/social-network-connection.observable'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import ChecklistComponent from '../checklist/checklist.component'
import { Code } from '../tutorial-instagram/hooks/use-new-tutorial-instagram/use-new-tutorial-instagram.hook'
import HeaderSkipped from './components/header-skipped/header-skipped'
import HeaderComponent from './components/header/header.component'
import ErrorVinculate from './enums/error-vinculate.enum'

import './error-vinculate.component.scoped.scss'

interface Props {
  height: number
  width: number
  onCloseModal: () => Promise<void>
  onOpenTutorial: (stepCode: Code) => void
  error: IError
  setError: (error: IError) => void
  setModalStatus: (status: ModalStatusTypes) => void
  user: User
  socialNetwork: SocialNetworksAvailables
}

export default ({ height, width, onCloseModal, error, setError, onOpenTutorial, setModalStatus, user, socialNetwork }: Props) => {
  const actionsContainer = useRef<HTMLDivElement>(null)
  const modalContainer = useRef<HTMLDivElement>(null)
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | null>(null)

  const [checkList, setCheckList] = useState([])
  const [isSkippedTutorial, setIsSkippedTutorial] = useState(false)
  const [urlCreators, setUrlCreators] = useState<string>('')
  const [businessProfileSelected, setBusinessProfileSelected] = useState<FacebookDiscoveryUser | null>(null)

  const [t] = useTranslation('modalVinculateSocialNetwork')

  const errorCode = useMemo(() => {
    if (error?.code) {
      return error.code
    }
    return error?.message
  }, [error])

  const buildCheckList = useCallback(
    async (skip?: boolean, hasBusinessProfileSelected?: boolean) => {
      const list = []
      switch (errorCode) {
        case 'HC002':
        case 'FB001':
          if (hasBusinessProfileSelected) {
            list.push({ status: 'success', message: t('modalError.success.isBusinessAccount') })
          } else {
            list.push({ status: 'error', message: t('modalError.errors.isBusinessAccount') })
          }
          list.push({ status: 'error', message: t(`modalError.errors.${ErrorMessage.FB001}`) })
          break
        case 'no-fb-pages-linked':
          if (hasBusinessProfileSelected) {
            if (!skip) {
              list.push({ status: 'success', message: t('modalError.success.isBusinessAccount') })
            }
            list.push({ status: 'error', message: t('modalError.errors.maybePageNotConnected') })
            list.push({ status: 'error', message: t(`modalError.errors.${ErrorMessage.FB001}`) })
          }
          break
        case 'FB004':
        case 'HC004':
        case 'no-fb-pages':
          if (!skip) {
            list.push({ status: 'success', message: t('modalError.success.isBusinessAccount') })
          }

          if (errorCode === 'no-fb-pages' || errorCode === 'HC004') {
            list.push({ status: 'error', message: t('modalError.errors.noProfileLinked') })
            list.push({ status: 'success', message: t(`modalError.success.${ErrorMessage.FB001}`) })
          } else {
            list.push({ status: 'error', message: t('modalError.errors.pageNotConnected') })
            list.push({ status: 'error', message: t(`modalError.errors.${ErrorMessage.FB001}`) })
          }
          break
        default:
          list.push({ status: 'error', message: t('modalError.errors.isBusinessAccount') })
          list.push({ status: 'error', message: t('modalError.errors.pageNotConnected') })
          list.push({ status: 'error', message: t(`modalError.errors.${ErrorMessage.FB001}`) })
          break
      }
      setCheckList(list)
    },
    [errorCode, t]
  )

  const initialize = useCallback(async () => {
    let isSkippedTutorial = false
    let hasBusinessProfileSelected = false
    const checkIsSkippedTutorial = async () => {
      isSkippedTutorial = await localForageHelper.getItem('isSkippedTutorial')
      if (isSkippedTutorial) {
        await localForageHelper.removeItem('businessProfileSelected')
        setIsSkippedTutorial(isSkippedTutorial ?? false)
      }
    }

    const getBusinessProfileSelected = async () => {
      const businessProfileSelected = (await localForageHelper.getItem('businessProfileSelected')) as FacebookDiscoveryUser
      if (businessProfileSelected) {
        hasBusinessProfileSelected = true
        setBusinessProfileSelected(businessProfileSelected)
      }
    }

    await checkIsSkippedTutorial()
    await getBusinessProfileSelected()
    await buildCheckList(isSkippedTutorial, hasBusinessProfileSelected)
  }, [buildCheckList])

  useEffect(() => {
    buildCheckList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode])

  useEffect(() => {
    initialize()
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeLocalStorageDataErrors = useCallback(async () => {
    await localForageHelper.removeItem('businessProfileSelected')
    await localForageHelper.removeItem('isSkippedTutorial')
  }, [])

  const handleReviewTutorial = useCallback(
    async (stepCode: Code) => {
      if (onOpenTutorial && typeof onOpenTutorial === 'function') {
        onOpenTutorial(stepCode)
      }
    },
    [onOpenTutorial]
  )

  const handleToGoConnections = useCallback(async () => {
    await removeLocalStorageDataErrors()
    onCloseModal()
    history.pushState(null, '', '/profile/social-networks')
  }, [onCloseModal, removeLocalStorageDataErrors])

  const getProfileFromIdentites = (idProfile: string) => {
    return user.identities.find((identity) => identity.id === idProfile)
  }

  useLayoutEffect(() => {
    const resize = () => {
      if (actionsContainer.current && modalContainer.current) {
        const borderTop = 28
        const borderBottom = 42
        modalContainer.current.style.height = `calc(100% - ${actionsContainer.current.clientHeight + borderTop + borderBottom}px)`
      }
    }
    window.addEventListener('resize', () => resize())
    resize()
    return () => window.removeEventListener('resize', resize)
  })

  return (
    <div
      className="error-vinculate"
      style={{
        height: width < 991 ? `calc(${height}px - 2px)` : null,
      }}
    >
      <div className="content-modal" ref={modalContainer}>
        <i className="fa-regular fa-circle-xmark white" style={{ fontSize: '5rem' }}></i>
        <p className="h-1 message-error white" dangerouslySetInnerHTML={{ __html: t('modalError.title') }} />
      </div>
      <div className="actions-container" ref={actionsContainer}>
        <div>
          {isSkippedTutorial && !['profileAlreadyLinked', 'no-fb-pages-linked'].includes(errorCode) ? <HeaderSkipped /> : null}
          {!isSkippedTutorial &&
          ['no-fb-pages', 'no-fb-pages-linked', 'FB001', 'HC002', 'FB004'].includes(errorCode) &&
          businessProfileSelected ? (
            <HeaderComponent socialNetwork={socialNetwork} />
          ) : null}
          {!['profileAlreadyLinked', 'no-fb-pages-linked'].includes(errorCode) ? <ChecklistComponent checkList={checkList} /> : null}
          {errorCode === 'profileAlreadyLinked' ? (
            <div>
              <p className="h5">{t('modalError.alreadyConnected')}</p>
              <div className="profile-linked display-flex mb-3">
                <div className="icon-profile">
                  <img
                    src={businessProfileSelected?.profile_picture_url}
                    alt="Profile"
                    width="100%"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null
                      currentTarget.src = defaultImage
                    }}
                  />
                </div>
                <div className="content-info-profile">
                  <span className="username text-bold">@{businessProfileSelected?.username}</span>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--gold)' }}></i>
                </div>
              </div>
            </div>
          ) : null}
          {errorCode === 'no-fb-pages-linked' ? <p className="text-center mb-3">{t('modalError.allProfileAlreadyConnected')}</p> : null}
        </div>
        <div className="actions">
          {!['profileAlreadyLinked', 'no-fb-pages-linked'].includes(errorCode) && isSkippedTutorial ? (
            <Button
              width="100%"
              className="inverted"
              onClick={async () => {
                const idProfileRenewed = (await localForageHelper.getItem('idProfileRenewed')) as string
                if (idProfileRenewed) {
                  const identity = getProfileFromIdentites(idProfileRenewed)
                  if (identity) {
                    const profile = {
                      id: identity?.id,
                      name: identity?.name,
                      username: identity?.username,
                      profile_picture_url: identity?.picture,
                    }
                    await localForageHelper.setItem('businessProfileSelected', profile)
                    await localForageHelper.setItem('actions', { action: 'renew', idProfile: idProfileRenewed })
                  }
                }
                setModalStatus('username-to-connect')
                setError(null)
                await localForageHelper.removeItem('isSkippedTutorial')
              }}
            >
              {t('modalError.checkIfProfileIsBusiness')}
            </Button>
          ) : null}
          {!['profileAlreadyLinked'].includes(errorCode) && !isSkippedTutorial ? (
            <Button width="100%" className="inverted" onClick={async () => await handleReviewTutorial(ErrorVinculate?.[errorCode])}>
              {t('modalError.reviewTutorial')}
            </Button>
          ) : null}
          {!['profileAlreadyLinked'].includes(errorCode) && !isSkippedTutorial ? (
            <Button
              width="100%"
              color="transparent"
              borderColor="transparent"
              textColor="var(--primary_color)"
              onClick={async () => await handleToGoConnections()}
            >
              <span className="label-button">{t('toGoConnections')}</span>
            </Button>
          ) : null}
          {['profileAlreadyLinked'].includes(errorCode) || (['no-fb-pages-linked'].includes(errorCode) && !businessProfileSelected) ? (
            <Button
              width="100%"
              onClick={async () => {
                await localForageHelper.removeItem('isSkippedTutorial')
                await onCloseModal()
                await localForageHelper.setItem('actions', { action: 'addAccount' })
                onSocialNetworkConnection.next({ connect: true })
              }}
            >
              {t('modalError.registerOtherProfile')}
            </Button>
          ) : null}
          {['profileAlreadyLinked'].includes(errorCode) || (['no-fb-pages-linked'].includes(errorCode) && !businessProfileSelected) ? (
            <a
              href={`${urlCreators}/v1/docs/como-descubro-a-empresa-correta-para-a-conex%C3%A3o-do-instagram-com-a-squid`}
              target="_blank"
              className="text-center"
              color="var(--primary_color)"
            >
              {t('modalError.needHelp')}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}
